import React, {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useState,
} from "react";
import { defineMessages, useIntl } from "react-intl";
import {
  Button,
  Checkbox,
  DateInput,
  Form,
  FormField,
  PhoneField,
  Textfield,
} from "@natera/form";
import {
  useErrorController,
  usePhoneValidation,
} from "@natera/platform/lib/hooks";
import {
  PartnerInformationFormData,
  PartnerInformationFormFields,
} from "@app/neva/constant/partnerInformationTypes";
import InfoIconSvg from "@app/neva/assets/svg/info-icon.svg";
import CalendarIconSvg from "@app/neva/assets/svg/calendar-icon.svg";

import "./inputForm.scss";
import Svg from "@natera/material/lib/svg";
import { convertDate, validateEmail } from "@app/utils";
import {
  APIContext,
  EventsContext,
  PartnerInformationContext,
} from "@app/neva/provider";
import { generateActionUserEvent } from "@app/neva/helpers";
import { ActionTypes, ButtonTypes } from "@app/neva/models";
import { PartnerInformationData } from "@app/neva/hooks/useApiController";
import { usePartnerInfoEvents } from "@app/neva/hooks/usePartnerInfoEvents";

const UNITED_STATES_COUNTRY_CODE = "US";

const messages = defineMessages({
  infoBlockText: {
    id: "partnerInformationInputFormInfoBlockText",
    defaultMessage:
      "Please provide either an email or phone number for your partner to continue.",
  },
  firstNameLabel: {
    id: "partnerInformationInputFormFirstNameLabel",
    defaultMessage: "First Name",
  },
  firstNamePlaceholder: {
    id: "partnerInformationInputFormFirstNamePlaceholder",
    defaultMessage: "Enter your partner’s First Name",
  },
  lastNameLabel: {
    id: "partnerInformationInputFormLastNameLabel",
    defaultMessage: "Last Name",
  },
  lastNamePlaceholder: {
    id: "partnerInformationInputFormLastNamePlaceholder",
    defaultMessage: "Enter your partner’s Last Name",
  },
  dateOfBirthLabel: {
    id: "partnerInformationInputFormDateOfBirthLabel",
    defaultMessage: "Date of Birth",
  },
  dateOfBirthPlaceholder: {
    id: "partnerInformationInputFormDateOfBirthPlaceholder",
    defaultMessage: "MM/DD/YYYY",
  },
  mobileNumberLabel: {
    id: "partnerInformationInputFormMobileNumberLabel",
    defaultMessage: "Mobile Number",
  },
  mobileNumberPlaceholder: {
    id: "partnerInformationInputFormMobileNumberPlaceholder",
    defaultMessage: "(XXX)-XXX-XXXX",
  },
  emailLabel: {
    id: "partnerInformationInputFormEmailLabel",
    defaultMessage: "Email Address",
  },
  emailPlaceholder: {
    id: "partnerInformationInputFormEmailPlaceholder",
    defaultMessage: "name@example.com",
  },
  consentLabel: {
    id: "partnerInformationInputFormConsentLabel",
    defaultMessage: "Consent to Share Results with Partner",
  },
  consentTextLabel: {
    id: "partnerInformationInputFormConsentTextLabel",
    defaultMessage:
      "I consent to Natera sharing my results with my reproductive partner.",
  },
  formButton: {
    id: "partnerInformationInputFormFormButton",
    defaultMessage: "Continue",
  },
  emptyFieldError: {
    id: "partnerInformationInputFormValidationErrorsEmptyField",
    defaultMessage: "This field is required",
  },
  invalidMobileNumber: {
    id: "partnerInformationInputFormValidationErrorsInvalidMobileNumber",
    defaultMessage: "Invalid mobile number",
  },
  phoneOrEmailRequired: {
    id: "partnerInformationInputFormValidationErrorsPhoneOrEmailRequired",
    defaultMessage: "Phone or email required",
  },
  emailOrPhoneRequired: {
    id: "partnerInformationInputFormValidationErrorsEmailOrPhoneRequired",
    defaultMessage: "Email or phone required",
  },
  invalidEmailAddress: {
    id: "partnerInformationInputFormValidationErrorsInvalidEmailAddress",
    defaultMessage: "Invalid email address",
  },
});

interface Props {
  setShowSuccessScreen: Dispatch<SetStateAction<boolean>>;
  messageId: string;
  type: string;
}

export const InputForm: FC<Props> = ({
  setShowSuccessScreen,
  messageId,
  type,
}) => {
  const { sendPartnerInformation, updatePartnerInformation } =
    useContext(APIContext);
  const { userEventHandler, setPassedOrSkippedFlowComponentsId } =
    useContext(EventsContext);
  const { partnerInformationForm, setPartnerInformationForm } = useContext(
    PartnerInformationContext
  );
  const { getValidationError, clearValidationError, setValidationError } =
    useErrorController();
  const [consentError, setConsentError] = useState(false);
  const [loading, setIsLoading] = useState(false);
  const [localPartnerInformationForm, setLocalPartnerInformationForm] =
    useState(partnerInformationForm);
  const intl = useIntl();
  const { validatePhone } = usePhoneValidation();
  const { logFormSubmitEvent } = usePartnerInfoEvents();

  const isPartnerFormUnchanged = (
    currentFormData: PartnerInformationFormData,
    previousFormData: PartnerInformationFormData
  ) => {
    return (
      currentFormData.firstName === previousFormData.firstName &&
      currentFormData.lastName === previousFormData.lastName &&
      currentFormData.birthDate === previousFormData.birthDate &&
      currentFormData.phoneNumber === previousFormData.phoneNumber &&
      currentFormData.email === previousFormData.email &&
      currentFormData.consent === previousFormData.consent
    );
  };

  const submitHandler: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();

    if (
      type === ButtonTypes.EDIT_PARTNER_INFORMATION &&
      isPartnerFormUnchanged(
        localPartnerInformationForm,
        partnerInformationForm
      )
    ) {
      setShowSuccessScreen(true);
      return;
    }

    const formData = new FormData(e.currentTarget);

    const firstName = formData
      .get(PartnerInformationFormFields.FIRST_NAME)
      ?.toString()
      .trim();
    const lastName = formData
      .get(PartnerInformationFormFields.LAST_NAME)
      ?.toString()
      .trim();
    const birthDate = formData
      .get(PartnerInformationFormFields.BIRTH_DATE)
      ?.toString();
    const phoneNumber = formData
      .get(PartnerInformationFormFields.PHONE_NUMBER)
      ?.toString();
    const email = formData.get(PartnerInformationFormFields.EMAIL)?.toString();
    const consent = formData
      .get(PartnerInformationFormFields.CONSENT)
      ?.toString();

    if (!firstName) {
      setValidationError(
        PartnerInformationFormFields.FIRST_NAME,
        intl.formatMessage(messages.emptyFieldError)
      );
    }

    if (!lastName) {
      setValidationError(
        PartnerInformationFormFields.LAST_NAME,
        intl.formatMessage(messages.emptyFieldError)
      );
    }

    if (!birthDate) {
      setValidationError(
        PartnerInformationFormFields.BIRTH_DATE,
        intl.formatMessage(messages.emptyFieldError)
      );
    }

    if (!consent) {
      setConsentError(true);
    }

    if (!phoneNumber && !email) {
      setValidationError(
        PartnerInformationFormFields.PHONE_NUMBER,
        intl.formatMessage(messages.phoneOrEmailRequired)
      );
    }

    if (!email && !phoneNumber) {
      setValidationError(
        PartnerInformationFormFields.EMAIL,
        intl.formatMessage(messages.emailOrPhoneRequired)
      );
    }

    if (
      phoneNumber &&
      !validatePhone(phoneNumber, UNITED_STATES_COUNTRY_CODE)
    ) {
      setValidationError(
        PartnerInformationFormFields.PHONE_NUMBER,
        intl.formatMessage(messages.invalidMobileNumber)
      );
    }

    if (email && !validateEmail(email)) {
      setValidationError(
        PartnerInformationFormFields.EMAIL,
        intl.formatMessage(messages.invalidEmailAddress)
      );
    }

    if (
      !firstName ||
      !lastName ||
      !birthDate ||
      !consent ||
      (!phoneNumber && !email) ||
      (phoneNumber &&
        !validatePhone(phoneNumber, UNITED_STATES_COUNTRY_CODE)) ||
      (email && !validateEmail(email))
    ) {
      return;
    }

    setIsLoading(true);
    logFormSubmitEvent();
    setPartnerInformationForm(localPartnerInformationForm);
    processPartnerInfo();
  };

  const processPartnerInfo = async () => {
    const partnerInformationPayload: PartnerInformationData = {
      ...localPartnerInformationForm,
      birthDate: convertDate(localPartnerInformationForm.birthDate) ?? "",
    };

    try {
      const res = await (type === ButtonTypes.ENTER_PARTNER_INFORMATION
        ? sendPartnerInformation(partnerInformationPayload)
        : updatePartnerInformation(partnerInformationPayload));

      if (res.status === 204) {
        setPassedOrSkippedFlowComponentsId(messageId);
        setIsLoading(false);
        setShowSuccessScreen(true);
        userEventHandler(
          generateActionUserEvent(ActionTypes.CONFIRM_PARTNER_INFO)
        );
      }
    } catch (error) {
      console.error("Failed to send partner information", error);
    }
  };

  const onChangeHandler = (
    target: PartnerInformationFormFields,
    value: string | boolean | Date
  ) => {
    if (
      target === PartnerInformationFormFields.EMAIL ||
      target === PartnerInformationFormFields.PHONE_NUMBER
    ) {
      clearValidationError(PartnerInformationFormFields.EMAIL);
      clearValidationError(PartnerInformationFormFields.PHONE_NUMBER);
    } else {
      clearValidationError(target);
    }

    if (target === PartnerInformationFormFields.CONSENT) {
      setConsentError(false);
    }

    setLocalPartnerInformationForm((prev) => ({
      ...prev,
      [target]: value,
    }));
  };

  return (
    <div className="input-form" data-testid="partner__input-form">
      <div className="input-form__info-block">
        <div className="input-form__icon-container">
          <Svg
            className="input-form__info-icon"
            content={InfoIconSvg}
            role="img"
          />
        </div>
        <p className="input-form__info-block-text">
          {intl.formatMessage(messages.infoBlockText)}
        </p>
      </div>

      <Form onSubmit={submitHandler}>
        <FormField
          className="input-form__form-field"
          label={intl.formatMessage(messages.firstNameLabel)}
          required
          htmlFor={PartnerInformationFormFields.FIRST_NAME}
          error={getValidationError(PartnerInformationFormFields.FIRST_NAME)}
        >
          <Textfield
            placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
            id={PartnerInformationFormFields.FIRST_NAME}
            name={PartnerInformationFormFields.FIRST_NAME}
            outline
            required={false}
            maxLength={255}
            fullWidth
            value={
              localPartnerInformationForm[
                PartnerInformationFormFields.FIRST_NAME
              ]
            }
            onChange={(e) =>
              onChangeHandler(
                PartnerInformationFormFields.FIRST_NAME,
                e.target.value
              )
            }
          />
        </FormField>

        <FormField
          className="input-form__form-field"
          label={intl.formatMessage(messages.lastNameLabel)}
          required
          htmlFor={PartnerInformationFormFields.LAST_NAME}
          error={getValidationError(PartnerInformationFormFields.LAST_NAME)}
        >
          <Textfield
            placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
            id={PartnerInformationFormFields.LAST_NAME}
            name={PartnerInformationFormFields.LAST_NAME}
            outline
            required={false}
            maxLength={255}
            fullWidth
            value={
              localPartnerInformationForm[
                PartnerInformationFormFields.LAST_NAME
              ]
            }
            onChange={(e) =>
              onChangeHandler(
                PartnerInformationFormFields.LAST_NAME,
                e.target.value
              )
            }
          />
        </FormField>

        <FormField
          className="input-form__form-field"
          label={intl.formatMessage(messages.dateOfBirthLabel)}
          required
          htmlFor={PartnerInformationFormFields.BIRTH_DATE}
          error={getValidationError(PartnerInformationFormFields.BIRTH_DATE)}
        >
          <DateInput
            id={PartnerInformationFormFields.BIRTH_DATE}
            name={PartnerInformationFormFields.BIRTH_DATE}
            placeholder={intl.formatMessage(messages.dateOfBirthPlaceholder)}
            required={false}
            maxDate={new Date()}
            outline
            trailingIcon={CalendarIconSvg}
            fullWidth
            date={
              localPartnerInformationForm[
                PartnerInformationFormFields.BIRTH_DATE
              ]
            }
            onDateChange={(date) =>
              onChangeHandler(
                PartnerInformationFormFields.BIRTH_DATE,
                date ?? ""
              )
            }
          />
        </FormField>

        <FormField
          className="input-form__form-field"
          label={intl.formatMessage(messages.mobileNumberLabel)}
          htmlFor={PartnerInformationFormFields.PHONE_NUMBER}
          error={getValidationError(PartnerInformationFormFields.PHONE_NUMBER)}
        >
          <PhoneField
            id={PartnerInformationFormFields.PHONE_NUMBER}
            name={PartnerInformationFormFields.PHONE_NUMBER}
            placeholder={intl.formatMessage(messages.mobileNumberPlaceholder)}
            autoComplete="tel"
            outline
            fullWidth
            value={
              localPartnerInformationForm[
                PartnerInformationFormFields.PHONE_NUMBER
              ]
            }
            onPhoneChange={(phone) =>
              onChangeHandler(
                PartnerInformationFormFields.PHONE_NUMBER,
                phone ?? ""
              )
            }
          />
        </FormField>

        <FormField
          className="input-form__form-field"
          label={intl.formatMessage(messages.emailLabel)}
          htmlFor={PartnerInformationFormFields.EMAIL}
          error={getValidationError(PartnerInformationFormFields.EMAIL)}
        >
          <Textfield
            outline
            placeholder={intl.formatMessage(messages.emailPlaceholder)}
            id={PartnerInformationFormFields.EMAIL}
            name={PartnerInformationFormFields.EMAIL}
            autoComplete="email"
            fullWidth
            value={
              localPartnerInformationForm[PartnerInformationFormFields.EMAIL]
            }
            onChange={(e) =>
              onChangeHandler(
                PartnerInformationFormFields.EMAIL,
                e.target.value
              )
            }
          />
        </FormField>

        <FormField
          className="input-form__form-field"
          label={intl.formatMessage(messages.consentLabel)}
          htmlFor={PartnerInformationFormFields.CONSENT}
          required
        >
          <Checkbox
            id="consent-checkbox"
            name={PartnerInformationFormFields.CONSENT}
            className="input-form__consent-checkbox"
            required={false}
            error={consentError}
            label={intl.formatMessage(messages.consentTextLabel)}
            checked={
              localPartnerInformationForm[PartnerInformationFormFields.CONSENT]
            }
            onChange={(e) =>
              onChangeHandler(
                PartnerInformationFormFields.CONSENT,
                e.target.checked
              )
            }
          />
        </FormField>

        <div className="input-form__button-container">
          <Button
            className="input-form__button"
            data-testid="partner__button"
            type="submit"
            unelevated
            loading={loading}
          >
            {intl.formatMessage(messages.formButton)}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default InputForm;
